import React from 'react';
import { LinearProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel } from '@mui/material';
import { UnfoldMore as UnfoldMoreIcon } from '@mui/icons-material';
import { TalkRoom } from '../../types/talk';
import { TABLE_THEME } from '../../types/common';
import TalkRoomTableRow from './TalkRoomTableRow';
import { CommonTablePagination } from '../common';

interface TalkRoomTableProps {
  talkRooms: TalkRoom[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  loading: boolean;
  sortFields: Array<{ field: string; direction: 'asc' | 'desc' }>;
  onPageChange: (event: unknown, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick: (nurseId: number, patientId: number) => void;
  onRequestSort: (property: string) => void;
}

const TalkRoomTable: React.FC<TalkRoomTableProps> = ({
  talkRooms,
  totalCount,
  page,
  rowsPerPage,
  loading,
  sortFields,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  onRequestSort
}) => {
  // カスタムソートアイコンコンポーネント（非アクティブ時のみ）
  const InactiveSortIcon = () => {
    return <UnfoldMoreIcon sx={{ fontSize: 16, color: '#bbb' }}/>;
  };
  // ソートフィールドの情報を取得
  const getSortInfo = (field: string) => {
    const sortField = sortFields.find(sf => sf.field === field);
    const sortIndex = sortFields.findIndex(sf => sf.field === field);
    return {
      isActive: !!sortField,
      direction: sortField?.direction || 'asc',
      index: sortIndex
    };
  };
  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', tableLayout: 'fixed' }}>
      {loading && <LinearProgress/>}
      <TableContainer>
        <Table stickyHeader aria-label="対話一覧" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={getSortInfo('talk_status').isActive ? getSortInfo('talk_status').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  color: TABLE_THEME.HEADER.TEXT,
                  width: '200px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('talk_status').isActive}
                  direction={getSortInfo('talk_status').direction}
                  onClick={() => onRequestSort('talk_status')}
                  IconComponent={!getSortInfo('talk_status').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  ステータス
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={getSortInfo('parent__name').isActive ? getSortInfo('parent__name').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  color: TABLE_THEME.HEADER.TEXT,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  minWidth: '200px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('parent__name').isActive}
                  direction={getSortInfo('parent__name').direction}
                  onClick={() => onRequestSort('parent__name')}
                  IconComponent={!getSortInfo('parent__name').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  看護師名 / ニックネーム
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={getSortInfo('child__name').isActive ? getSortInfo('child__name').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  color: TABLE_THEME.HEADER.TEXT,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  minWidth: '200px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('child__name').isActive}
                  direction={getSortInfo('child__name').direction}
                  onClick={() => onRequestSort('child__name')}
                  IconComponent={!getSortInfo('child__name').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  患者名 / ニックネーム
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={getSortInfo('session_price').isActive ? getSortInfo('session_price').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  color: TABLE_THEME.HEADER.TEXT,
                  width: '150px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('session_price').isActive}
                  direction={getSortInfo('session_price').direction}
                  onClick={() => onRequestSort('session_price')}
                  IconComponent={!getSortInfo('session_price').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  プラン
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sortDirection={getSortInfo('session_start_at').isActive ? getSortInfo('session_start_at').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  color: TABLE_THEME.HEADER.TEXT,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  width: '200px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('session_start_at').isActive}
                  direction={getSortInfo('session_start_at').direction}
                  onClick={() => onRequestSort('session_start_at')}
                  IconComponent={!getSortInfo('session_start_at').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  期間
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  color: TABLE_THEME.HEADER.TEXT,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  width: '120px'
                }}
              >
                経過日数
              </TableCell>
              <TableCell
                align="center"
                sortDirection={getSortInfo('last_chat_send_at').isActive ? getSortInfo('last_chat_send_at').direction : false}
                sx={{
                  fontWeight: 'bold',
                  backgroundColor: TABLE_THEME.HEADER.BACKGROUND,
                  color: TABLE_THEME.HEADER.TEXT,
                  borderBottom: `2px solid ${TABLE_THEME.HEADER.BORDER}`,
                  minWidth: '200px'
                }}
              >
                <TableSortLabel
                  active={getSortInfo('last_chat_send_at').isActive}
                  direction={getSortInfo('last_chat_send_at').direction}
                  onClick={() => onRequestSort('last_chat_send_at')}
                  IconComponent={!getSortInfo('last_chat_send_at').isActive ? InactiveSortIcon : undefined}
                  sx={{
                    '&.Mui-active': {
                      color: TABLE_THEME.HEADER.TEXT,
                      fontWeight: 'bold',
                      '& .MuiTableSortLabel-icon': {
                        opacity: '1 !important',
                        color: '#1976d2 !important'
                      }
                    }
                  }}
                >
                  最終チャット
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {talkRooms.map((room) => (
              <TalkRoomTableRow
                key={room.id}
                room={room}
                onClick={() => onRowClick(room.parent_id, room.child_id)}
              />
            ))}
            {talkRooms.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  対話データがありません
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CommonTablePagination
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  );
};
export default TalkRoomTable;
